import { useEffect, useRef, type PropsWithChildren } from 'react';

import { useKeepMountedContext } from '../../hooks/useKeepMountedContext.js';

/**
 * The `KeepMounted` component uses the `KeepMountedContext` to render the children into
 * a portal in order to avoid unmounting in-between renders.
 * Since the portal itself does not change and is only appended to the div rendered in the
 * `KeepMounted` component, even though the parent component may change, the children stay mounted.
 * The `KeepMounted` component has to be rendered all the time for this to work (e.g. when conditionally
 * rendering a component, if the component itself is not rendered, the `KeepMounted` component still
 * has to be rendered).
 * @internal
 */
export const KeepMounted = (props: PropsWithChildren<{ id: string }>) => {
  const { id, children } = props;

  const { getPortalElement } = useKeepMountedContext();
  const keepMountedRef = useRef<HTMLDivElement | null>(null);

  const appendPortalElement = () => {
    const portalElement = getPortalElement(id, children);
    // Append the already existing portal with all it's contents to the
    // container rendered by the `KeepMounted` component.
    portalElement && keepMountedRef.current?.appendChild(portalElement);
  };

  useEffect(() => {
    appendPortalElement();
    // Excluding `appendPortalElement`.
    // Depends on the children. As soon as they change, the effect runs anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return <div ref={keepMountedRef} />;
};
